'use client';

// Image de l'accroche d'une VIDÉO publiée (scène `jr-hook`, avant « JeRaisonne
// présente ») : la question que tout élève se pose, qui claque mot à mot.
// Calque opaque comme l'intro ; animé par le temps de la scène (`apply(t)`),
// mouvement dans `hook-motion.ts`.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { JRAF_PALETTE } from '@jeraisonne/animation';
import type { BookendHandle } from './bookend-layer';
import { hookFontSize, hookFrame, hookGroups, hookTokens } from './hook-motion';

export interface HookLayerProps {
  text: string;
  emphasis?: string | null | undefined;
  reducedMotion: boolean;
}

const C = JRAF_PALETTE;

export const HookLayer = forwardRef<BookendHandle, HookLayerProps>(function HookLayer(
  { text, emphasis, reducedMotion },
  ref,
) {
  const tokens = useMemo(() => hookTokens(text, emphasis), [text, emphasis]);
  const groups = useMemo(() => hookGroups(tokens), [tokens]);
  // une phrase par ligne : « Stœchiométrique ? / À tes souhaits ! »
  const sentenceEnd = (g: number) => tokens[groups[g]!.at(-1)!]!.marks;
  const tokenEls = useRef<(HTMLElement | null)[]>([]);
  const markEls = useRef<(HTMLElement | null)[][]>([]);
  const block = useRef<HTMLDivElement | null>(null);
  const flash = useRef<HTMLDivElement | null>(null);
  const rays = useRef<HTMLDivElement | null>(null);
  const bigMark = useRef<HTMLDivElement | null>(null);
  const lastT = useRef(0);

  const apply = (t: number) => {
    lastT.current = t;
    const f = hookFrame(tokens, t, reducedMotion);
    f.tokens.forEach((s, i) => {
      const el = tokenEls.current[i];
      if (!el) return;
      el.style.opacity = String(s.opacity);
      el.style.transform = s.transform;
    });
    let k = 0;
    tokens.forEach((tok, i) => {
      if (!tok.marks) return;
      const styles = f.marks[k++] ?? [];
      styles.forEach((s, j) => {
        const el = markEls.current[i]?.[j];
        if (!el) return;
        el.style.opacity = String(s.opacity);
        el.style.transform = s.transform;
      });
    });
    if (block.current) block.current.style.transform = f.shake;
    if (flash.current) flash.current.style.opacity = String(f.flash);
    if (rays.current) {
      rays.current.style.opacity = String(f.rays.opacity);
      rays.current.style.transform = f.rays.transform;
    }
    if (bigMark.current) {
      bigMark.current.style.opacity = String(f.bigMark.opacity);
      bigMark.current.style.transform = f.bigMark.transform;
    }
  };
  useImperativeHandle(ref, () => ({ apply }));
  useLayoutEffect(() => apply(lastT.current), [tokens, reducedMotion]);

  // taille estimée, puis ajustée si un mot déborde une fois la police chargée
  const [size, setSize] = useState(() => hookFontSize(text));
  const textRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    setSize(hookFontSize(text));
    let alive = true;
    const fit = () => {
      const el = textRef.current;
      const box = block.current;
      if (!alive || !el || !box || !box.clientWidth) return;
      const widest = Math.max(
        ...[...el.querySelectorAll<HTMLElement>('[data-hook-group]')].map((g) => g.offsetWidth), // largeur de mise en page : sans les zooms en cours
      );
      if (widest > box.clientWidth + 1)
        setSize((s) => Math.max(24, Math.floor((s * box.clientWidth) / widest)));
    };
    void (document.fonts?.ready ?? Promise.resolve()).then(fit);
    return () => {
      alive = false;
    };
  }, [text]);
  return (
    <div
      data-bookend="hook"
      className="absolute inset-0 flex items-center justify-center overflow-hidden px-7 text-center"
      style={{
        backgroundColor: C.night,
        backgroundImage:
          'radial-gradient(circle at 50% 46%, rgba(5,102,242,0.55) 0, rgba(5,102,242,0.12) 42%, rgba(5,102,242,0) 70%)',
        color: C.paper,
      }}
    >
      {/* rayons qui tournent doucement derrière la question */}
      <div
        ref={rays}
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[46%]"
        style={{
          width: 900,
          height: 900,
          opacity: 0,
          backgroundImage:
            'repeating-conic-gradient(from 0deg, rgba(242,193,78,0.13) 0deg 9deg, rgba(242,193,78,0) 9deg 24deg)',
          WebkitMaskImage: 'radial-gradient(circle, #000 0, #000 22%, transparent 62%)',
          maskImage: 'radial-gradient(circle, #000 0, #000 22%, transparent 62%)',
        }}
      />
      {/* grand « ? » en fond */}
      <div
        ref={bigMark}
        aria-hidden="true"
        className="font-display pointer-events-none absolute left-[76%] top-[20%] font-extrabold leading-none"
        style={{ fontSize: 330, color: 'rgba(142,180,255,0.16)', opacity: 0 }}
      >
        ?
      </div>

      <div ref={block} className="relative w-full max-w-[310px]">
        <p
          ref={textRef}
          className="font-display font-extrabold leading-[1.08] tracking-tight"
          style={{
            fontSize: size,
            textShadow: '0 5px 0 rgba(0,0,0,0.28), 0 14px 34px rgba(0,0,0,0.35)',
          }}
        >
          {groups.map((group, g) => (
            <span key={g}>
              {g > 0 && (sentenceEnd(g - 1) ? <br /> : ' ')}
              <span data-hook-group className="inline-block whitespace-nowrap">
                {group.map((i, k) => {
                  const tok = tokens[i]!;
                  return (
                    <span key={i}>
                      {k > 0 && !tok.glued ? '\u202f' : ''}
                      <span
                        ref={(el) => {
                          tokenEls.current[i] = el;
                        }}
                        className="inline-block"
                        style={{
                          opacity: 0,
                          transformOrigin: '50% 60%',
                          color: tok.emphasis || tok.marks ? C.gold : undefined,
                          textShadow: tok.emphasis
                            ? '0 0 22px rgba(242,193,78,0.55), 0 5px 0 rgba(0,0,0,0.28)'
                            : undefined,
                        }}
                      >
                        {tok.marks
                          ? [...tok.text].map((ch, j) => (
                              <span
                                key={j}
                                ref={(el) => {
                                  (markEls.current[i] ??= [])[j] = el;
                                }}
                                className="inline-block"
                                style={{ opacity: 0, transformOrigin: '50% 85%' }}
                              >
                                {ch}
                              </span>
                            ))
                          : tok.text}
                      </span>
                    </span>
                  );
                })}
              </span>
            </span>
          ))}
        </p>
      </div>

      {/* éclair quand le premier mot claque */}
      <div
        ref={flash}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ backgroundColor: '#ffffff', opacity: 0 }}
      />
    </div>
  );
});
